<script>
export default {
	name: 'MobileMainMenuFooter',
	expose: [],
	inheritAttrs: false,
};
</script>

<script setup>
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import AppFooterAccordion from '~/components/AppFooter/AppFooterAccordion.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import OverlayDrawer from '~/components/OverlayDrawer.vue';
import { useRouter, useRoute } from 'vue-router';
import CTAButton from '~/aem-components/CTAButton.vue';
import ExperienceFragment from '~/aem-components/ExperienceFragment.vue';

const { isEditorMode } = useGlobalAEMState();

const props = defineProps({
	generalTab: { type: Object, default: () => ({}) },
	exerciseYourRightsTab: { type: Object, default: () => ({}) },
	subsidiaryTab: { type: Object, default: () => ({}) },
	socialMediaTab: { type: Object, default: () => ({}) },
	tncTab: { type: Object, default: () => ({}) },
	copyrightTab: { type: Object, default: () => ({}) },
	componentIdAA: { type: String, default: '' },
	newsletterTab: { type: Object, default: () => ({}) },
	enableHeaderRafiGlassDesign: { type: Boolean, default: false },
});

const router = useRouter();
const route = useRoute();
const showNewsLetter = ref(false);

const emit = defineEmits([
	'update:currentActiveAccordion',
]);

const footerAccordionRef = ref(null);

const isPropsDataEmpty = () => {
	const exerciseYourRights = Object.keys(props.exerciseYourRightsTab).length === 0;
	const subsidiary = Object.keys(props.subsidiaryTab).length === 0;
	const socialMedia = Object.keys(props.socialMediaTab).length === 0;
	const tnc = Object.keys(props.tncTab).length === 0;
	const copyright = Object.keys(props.copyrightTab).length === 0;

	return exerciseYourRights && subsidiary && socialMedia && tnc && copyright;
};

const setFooterCurrentAccordion = (newValue) => {
	footerAccordionRef.value?.setCurrentAccordion(newValue);
};

defineExpose({
	setFooterCurrentAccordion,
});

const openNewsletterOverlay = () => {
	showNewsLetter.value = true;
	showOverlay.value = true;
	router.replace({ query: {
		...route.query,
		acemail: undefined,
		id: undefined,
	} });
};

const showOverlay = ref(
	window.location.hash == '#manage-subscription' ? true : false
);

const resetOverlayValues = () => {
	showOverlay.value = false;
	showNewsLetter.value = false;
};
</script>

<template>
<template v-if="isPropsDataEmpty()">
	<p v-if="isEditorMode" class="text-center">🔧 Please click here to configure footer</p>
</template>
<template v-else>
	<footer
		v-if="(isMobileViewport && !props.generalTab.isHideInMobile)"
		class="MobileMainMenuFooter"
	>
		<div
			class="footer-inner-container bg-$overlay-container-bg-color"
			:class="{ '>md:rounded-t-7xl': props.generalTab.isCurvedCorners }"
		>
			<div class="footer-inner-wrapper gap-x-4 pb-6 >md:pt-16 >md:pb-12 text-white">
				<div :class="props.enableHeaderRafiGlassDesign ? 'footer-topNew' : 'footer-top'">
					<AppFooterAccordion
						v-if="!props.enableHeaderRafiGlassDesign"
						ref="footerAccordionRef"
						:items="props.generalTab.navigationMenu"
						@update:current-active-accordion="$emit('update:currentActiveAccordion', $event)"
					/>
					<div
						:class="{ 'hidden': props.newsletterTab.isHideInMobile }"
						class="footer-newsletter-container mt-5"
					>
						<p class="font-semibold">{{ props.newsletterTab.text }}</p>
						<div class="mt-2">
							<CTAButton
								v-if="props.newsletterTab.subscribeNowButtonLabel"
								:onClick="openNewsletterOverlay"
								:ctaText="props.newsletterTab.subscribeNowButtonLabel"
								alignmentDesktop="left"
								alignmentMobile="left"
								styleType="secondary"
							/>
						</div>
					</div>
				</div>
				<div class="footer-bottom">
					<div class=">lg:w-5/12 flex items-end">
						<div class="bottom-left-col">
							<div v-if="Object.entries(props.exerciseYourRightsTab).length > 0" class="rights-logo-wrapper flex gap-x-6 gap-y-2.5">
								<!-- <img
									v-for="item in props.exerciseYourRights"
									:key="item.imagePath"
									:src="item.imagePath"
									class="rights-logo h-[120px] w-auto inline-block"
									:alt="item.imageAltText"
								/> -->
								<AppHyperlink
									v-aa="[
										{
											clickName: props.exerciseYourRightsTab.image1.altText,
											clickComponentType: 'URL',
											componentName: 'AppFooter',
											componentID: props.componentIdAA,
										},
										{
											name: props.exerciseYourRightsTab.image1.altText,
											type: isUrlMatchCurrentHostname(props.exerciseYourRightsTab.image1.url) ? 'other' : 'exit',
										},
									]"
									:href="props.exerciseYourRightsTab.image1.url"
									:target="props.exerciseYourRightsTab.image1.isOpenNewTab ? '_blank' : '_self'"
									rel="noopener noreferrer"
									class="rights-logo-anchor"
									:showExternalLinkIcon="false"
								>
									<img
										:src="props.exerciseYourRightsTab.image1.path"
										class="rights-logo inline-block"
										:alt="props.exerciseYourRightsTab.image1.altText"
									/>
								</AppHyperlink>
								<AppHyperlink
									v-aa="[
										{
											clickName: props.exerciseYourRightsTab.image2.altText,
											clickComponentType: 'URL',
											componentName: 'AppFooter',
											componentID: props.componentIdAA,
										},
										{
											name: props.exerciseYourRightsTab.image2.altText,
											type: isUrlMatchCurrentHostname(props.exerciseYourRightsTab.image2.url) ? 'other' : 'exit',
										},
									]"
									:href="props.exerciseYourRightsTab.image2.url"
									:target="props.exerciseYourRightsTab.image2.isOpenNewTab ? '_blank' : '_self'"
									rel="noopener noreferrer"
									class="rights-logo-anchor"
									:showExternalLinkIcon="false"
								>
									<img
										:src="props.exerciseYourRightsTab.image2.path"
										class="rights-logo inline-block"
										:alt="props.exerciseYourRightsTab.image2.altText"
									/>
								</AppHyperlink>
							</div>
							<div class="partner-logo-wrapper">
								<AppHyperlink
									v-for="item in props.subsidiaryTab.subsidiaryList"
									:key="item.imageAltText"
									v-aa="[
										{
											clickName: item.imageAltText,
											clickComponentType: 'URL',
											componentName: 'AppFooter',
											componentID: props.componentIdAA,
										},
										{
											name: item.imageAltText,
											type: isUrlMatchCurrentHostname(item.imageUrl) ? 'other' : 'exit',
										},
									]"
									:href="item.imageUrl"
									:target="item.isOpenNewTab ? '_blank' : '_self'"
									class="partner-logo inline-block"
									:class="{ 'hidden': (isMobileViewport && item.isHideInMobile) || (!isMobileViewport && item.isHideInDesktop) }"
									:showExternalLinkIcon="false"
								>
									<img
										:src="item.image"
										class="h-[40px] w-auto inline-block"
										:alt="item.imageAltText"
									/>
								</AppHyperlink>
							</div>
							<p v-html-sanitize="props.copyrightTab.text" class="copyright-text text-neutral-grey-extradark text-xs font-bold >lg:hidden >md:w-11/12 lg:w-full mx-auto mt-8"></p>
						</div>
					</div>
					<div class=">lg:w-7/12 >lg:flex flex-col items-end justify-end">
						<div class="bottom-right-col">
							<div class="social-icons-wrapper">
								<p :class="['text-xs', 'font-bold', 'mb-4', 'lg:mt-10', props.enableHeaderRafiGlassDesign ? 'text-neutral-grey-extradarkNew' : 'text-neutral-grey-extradark']">{{ props.socialMediaTab.label }}</p>
								<ul class="social-icons-list >lg:text-right >lg:flex >lg:items-center">
									<li
										v-for="socialItem in props.socialMediaTab.socialMediaList"
										:key="socialItem.iconImage"
										class="social-icons-list-item inline-block w-8 h-8"
									>
										<AppHyperlink
											v-aa="[
												{
													clickName: socialItem.iconAltText,
													clickComponentType: 'URL',
													componentName: 'AppFooter',
													componentID: props.componentIdAA,
												},
												{
													name: socialItem.iconAltText,
													type: 'other',
												},
											]"
											class="social-icons-anchor"
											:class="{
												'align-middle': socialItem.iconType !== 'icon-font-awesome-id',
											}"
											:href="socialItem.iconUrl"
											:target="socialItem.isOpenNewTab ? '_blank' : '_self'"
											rel="noopener noreferrer"
											:showExternalLinkIcon="false"
										>

											<FontAwesomeLazy
												v-if="socialItem.iconType === 'icon-font-awesome-id'"
												class="social-icon-logo hover:fill-primary-blue-extralight mx-auto"
												:class="[props.enableHeaderRafiGlassDesign ? 'fill-neutral-white-baseNew' : 'fill-neutral-white-base']"
												:iconId="socialItem.iconId"
												role="presentation"
											/>

											<img
												v-else
												:src="socialItem.iconImage"
												class="social-icons-image w-8 h-8 hover:opacity-50"
												:alt="socialItem.iconAltText"
												role="presentation"
											/>
											<span class="sr-only">{{ socialItem.iconAltText }}</span>
										</AppHyperlink>
									</li>
								</ul>
							</div>
							<div class="copyright-wrapper">
								<div class="copyright-list">
									<ul class="mb-4 md:mb-1 lg:flex lg:flex-col">
										<li v-for="(item, index) in props.tncTab.tncList" :key="`${item.title}-${index}`" class="copyright-list inline-block">
											<AppHyperlink
												v-aa="[
													{
														clickName: item.title,
														clickComponentType: 'URL',
														componentName: 'AppFooter',
														componentID: props.componentIdAA,
													},
													{
														name: item.title,
														type: 'other',
													},
												]"
												class="copyright-link text-neutral-white-base hover:text-primary-blue-extralight"
												:class="[props.enableHeaderRafiGlassDesign ? 'text-neutral-white-baseNew' : 'text-neutral-white-base']"
												:href="item.titleUrl"
												:target="item.isOpenNewTab ? '_blank' : '_self'"
												rel="noopener noreferrer"
												:showExternalLinkIcon="false"
											>
												<span class="copyright-link-text">{{ item.title }}</span>
											</AppHyperlink>
										</li>
									</ul>
									<p v-html-sanitize="props.copyrightTab.text" class="copyright-text text-neutral-grey-extradark text-xs font-bold lg:hidden"></p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</footer>
</template>
<OverlayDrawer
	:isVisible="showOverlay"
	@update:is-visible="showOverlay = $event ; showFloatingActionList = $event"
>
	<div class="h-full">
		<div class="drawer-header">
			<img src="@/assets/logo-mh.svg" class="h-6 >md:hidden" />
			<button
				type="button"
				class="w-8 h-8 my-6 p-0 md:h-6 md:w-6 md:my-4 flex items-center justify-center"
				@click="resetOverlayValues"
			>
				<icon-far-xmark class="w-2.5" />
			</button>
		</div>
		<ExperienceFragment class="flex-grow" :xfPath="props.newsletterTab.newsletterXfPath" />
		<!-- <ExperienceFragment v-if="showFreeComponent" class="flex-grow ul-align-center" :xfPath="experienceFragmentPath" /> -->
	</div>
</OverlayDrawer>
</template>


<style scoped lang="scss">
@use '~/styles/partials/_var.scss';

.MobileMainMenuFooter {
	width: 100%;

	.footer-bottom {
		display: flex;
		flex-direction: column-reverse;

		@media #{var.$query-min-lg} {
			@apply mt-8;
			flex-direction: row;
		}

		.bottom-left-col {

			.partner-logo-wrapper {
				display: flex;
				flex-wrap: wrap;
				column-gap: 40px;
				row-gap: 16px;
				@apply mt-8;

				.AppHyperlink {
					width: 80px;
				}

				// .partner-logo {

				// 	&:not(:last-child) {
				// 		@apply mr-10;
				// 	}
				// }
			}
		}


		.bottom-right-col {
			flex-direction: column;
			align-items: flex-end;
			justify-content: end;

			@media #{var.$query-min-lg} {
				display: flex;
			}

			.social-icons-list {

				@media #{var.$query-max-md} {
					display: grid;
					grid-template-columns: repeat(5, 1fr);
					grid-column-gap: 40px;
					grid-row-gap: 24px;
				}
			}

			.social-icons-wrapper {
				margin-bottom: 42px;

				@media #{var.$query-max-md} {
					@apply mb-10;
					width: 100%;
				}

				.social-icons-list-item {

					&:not(:last-child) {
						@media #{var.$query-min-md} {
							@apply mr-8;
						}
					}

					.social-icons-image {
						display: inline-block;
					}
				}
			}

			.copyright-list {
				@apply font-semibold;

				&:not(:last-child) {
					@apply mb-5;

					@media #{var.$query-min-lg} {
						@apply mr-5;
					}
				}
			}
		}
	}

	.copyright-text {
		@apply break-words text-right;

		@media #{var.$query-max-md} {
			font-size: 10px;
			@apply mt-8 text-center;
		}
	}

	.copyright-link-text {
		font-size: 14px;
	}
	
	.rights-logo {
		height: 114px;
		width: auto;
	}
	.footer-topNew{
		color: #1F4074;
	}
	.fill-neutral-white-baseNew{
		fill:#0D4689;
	}
	.text-neutral-white-baseNew{
		color: #1F4074;
	}
	.text-neutral-white-baseNew:hover{
		color: #1F4074;
	}
	.text-neutral-grey-extradarkNew{
		color: #1F4074;
	}
}
.footer-topNew{
	.footer-newsletter-container{
		border: 1px solid #CED8DD;
		border-radius: 24px;
		padding: 16px;
		background-color: #FFFFFF;
		:deep(.CTAButton.styleType-secondary){
			font-weight: 600 !important;
		}
	}
}
</style>
